<template>
  <b-modal
    :title="title"
    modal-class="modal-primary"
    @hidden="$emit('hidden')"
    v-model="showModal"
    size="lg"
  >
    <ValidationObserver ref="form-empresa">
      <b-row>
        <!-- RUC -->
        <b-col md="6">
          <b-form-group>
            <template #label>
              <span>
                RUC
                <span class="text-danger">*</span>
              </span>
            </template>
            <ValidationProvider
              name="ruc"
              rules="required|max:11"
              #default="{ errors }"
            >
              <b-form-input
                v-model="form.ruc"
                type="text"
                placeholder="RUC"
                :class="{ 'border-danger rounded': errors.length }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>
        </b-col>

        <!-- Razón Social -->
        <b-col md="6">
          <b-form-group>
            <template #label>
              <span>
                Razón Social
                <span class="text-danger">*</span>
              </span>
            </template>
            <ValidationProvider
              name="razon_social"
              rules="required|max:255"
              #default="{ errors }"
            >
              <b-form-input
                v-model="form.razon_social"
                type="text"
                placeholder="Razón Social"
                :class="{ 'border-danger rounded': errors.length }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>
        </b-col>

        <!-- Nombre Comercial -->
        <b-col md="6">
          <b-form-group label="Nombre Comercial">
            <ValidationProvider
              name="nombre_comercial"
              rules="max:255"
              #default="{ errors }"
            >
              <b-form-input
                v-model="form.nombre_comercial"
                type="text"
                placeholder="Nombre Comercial"
                :class="{ 'border-danger rounded': errors.length }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>
        </b-col>

        <!-- Dirección -->
        <b-col md="6">
          <b-form-group label="Dirección">
            <ValidationProvider
              name="direccion"
              rules="max:255"
              #default="{ errors }"
            >
              <b-form-input
                v-model="form.direccion"
                type="text"
                placeholder="Dirección"
                :class="{ 'border-danger rounded': errors.length }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>
        </b-col>

        <!-- Teléfono -->
        <b-col md="6">
          <b-form-group label="Teléfono">
            <ValidationProvider
              name="telefono"
              rules="max:20"
              #default="{ errors }"
            >
              <b-form-input
                v-model="form.telefono"
                type="text"
                placeholder="Teléfono"
                :class="{ 'border-danger rounded': errors.length }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>
        </b-col>

        <!-- Correo -->
        <b-col md="6">
          <b-form-group label="Correo">
            <ValidationProvider
              name="correo"
              rules="email|max:255"
              #default="{ errors }"
            >
              <b-form-input
                v-model="form.correo"
                type="email"
                placeholder="Correo"
                :class="{ 'border-danger rounded': errors.length }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>
        </b-col>

        <!-- Logo -->
        <b-col md="6">
          <b-form-group label="Logo">
            <ValidationProvider
              name="logo"
              rules="file|max:2048"
              #default="{ errors }"
            >
              <!-- Vista previa -->
              <div class="mb-2" v-if="previewLogo || form.logo_url">
                <b-img
                  :src="previewLogo || form.logo_url"
                  fluid
                  rounded
                  alt="Logo de la empresa"
                  style="max-height: 150px"
                />
              </div>

              <!-- Input archivo -->
              <b-form-file
                :class="{ 'border-danger rounded': errors.length }"
                placeholder="Seleccionar archivo"
                @change="handleLogoChange"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>
        </b-col>

        <!-- Representante Legal -->
        <b-col md="6">
          <b-form-group label="Representante Legal">
            <ValidationProvider
              name="representante_legal"
              rules="max:255"
              #default="{ errors }"
            >
              <b-form-input
                v-model="form.representante_legal"
                type="text"
                placeholder="Representante Legal"
                :class="{ 'border-danger rounded': errors.length }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>
        </b-col>

        <!-- Banco Pago Planilla Principal -->
        <b-col md="6">
          <b-form-group label="Banco Pago Planilla Principal">
            <v-select
              v-model="form.cuenta_planilla_principal_id"
              :options="cuentasBancarias"
              :reduce="(val) => val.id"
              label="label"
              placeholder="Seleccionar cuenta bancaria"
              :clearable="true"
              class="w-100"
            />
          </b-form-group>
        </b-col>

        <!-- Banco Pago Bonificación Principal -->
        <b-col md="6">
          <b-form-group label="Banco Pago Bonificación Principal">
            <v-select
              v-model="form.cuenta_bono_principal_id"
              :options="cuentasBancarias"
              :reduce="(val) => val.id"
              label="label"
              placeholder="Seleccionar cuenta bancaria"
              :clearable="true"
              class="w-100"
            />
          </b-form-group>
        </b-col>

        <!-- Banco Pago Planilla RXH -->
        <b-col md="6">
          <b-form-group label="Banco Pago Planilla RXH">
            <v-select
              v-model="form.cuenta_planilla_adicional_id"
              :options="cuentasBancarias"
              :reduce="(val) => val.id"
              label="label"
              placeholder="Seleccionar cuenta bancaria"
              :clearable="true"
              class="w-100"
            />
          </b-form-group>
        </b-col>

        <!-- Banco Pago Bonificación Secundaria -->
        <b-col md="6">
          <b-form-group label="Banco Pago Bonificación Secundaria">
            <v-select
              v-model="form.cuenta_bono_adicional_id"
              :options="cuentasBancarias"
              :reduce="(val) => val.id"
              label="label"
              placeholder="Seleccionar cuenta bancaria"
              :clearable="true"
              class="w-100"
            />
          </b-form-group>
        </b-col>

        <!-- Estado (Switch) -->
        <b-col md="6">
          <b-form-group label="Estado">
            <ValidationProvider
              name="estado"
              rules="required"
              #default="{ errors }"
            >
              <b-form-checkbox
                v-model="form.estado"
                switch
                :value="1"
                :unchecked-value="0"
                :class="{ 'border border-danger rounded p-2': errors.length }"
              >
                {{ form.estado ? "Activo" : "Inactivo" }}
              </b-form-checkbox>
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>
        </b-col>
      </b-row>
    </ValidationObserver>

    <template #modal-footer>
      <b-container fluid>
        <b-row>
          <b-col class="d-flex justify-content-end p-0">
            <b-button
              variant="outline-danger"
              class="mr-1"
              @click="$emit('hidden')"
              v-t="'Cancel'"
            />
            <b-button variant="outline-success" @click="onSubmit">
              {{ empresaId ? $t("Update") : $t("Save") }}
            </b-button>
          </b-col>
        </b-row>
      </b-container>
    </template>
  </b-modal>
</template>

<script>
import EmpresaService from "@/views/settings/views/empresas/service/empresa.service.js";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  props: {
    empresaId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      showModal: true,
      previewLogo: null,
      cuentasBancarias: [],
      form: {
        ruc: null,
        razon_social: null,
        nombre_comercial: null,
        direccion: null,
        telefono: null,
        correo: null,
        logo: null,
        logo_url: null,
        representante_legal: null,
        cuenta_planilla_principal_id: null,
        cuenta_bono_principal_id: null,
        cuenta_planilla_adicional_id: null,
        cuenta_bono_adicional_id: null,
        estado: 1,
      },
    };
  },
  computed: {
    title() {
      return this.empresaId ? "EDITAR EMPRESA" : "CREAR EMPRESA";
    },
  },
  mounted() {
    this.loadCuentasBancarias();
    if (this.empresaId) this.loadEmpresa();
  },
  methods: {
    async loadCuentasBancarias() {
      try {
        const { data } = await EmpresaService.cuentasBancarias();
        this.cuentasBancarias = data || [];
      } catch (error) {
        console.error("Error al cargar cuentas bancarias", error);
      }
    },
    handleLogoChange(e) {
      const file = e.target.files[0];
      this.form.logo = file;

      // Si hay archivo, generar vista previa
      if (file) {
        this.previewLogo = URL.createObjectURL(file);
      } else {
        this.previewLogo = null;
      }
    },
    async loadEmpresa() {
      try {
        const { data } = await EmpresaService.show({ id: this.empresaId });
        data.logo = null;
        [
          "cuenta_planilla_principal_id",
          "cuenta_bono_principal_id",
          "cuenta_planilla_adicional_id",
          "cuenta_bono_adicional_id",
        ].forEach((key) => {
          if (data[key] !== null && data[key] !== undefined) {
            data[key] = Number(data[key]);
          }
        });
        this.form = { ...this.form, ...data };
      } catch (error) {
        console.error(error);
      }
    },
    async onSubmit() {
      try {
        this.addPreloader();

        const formData = new FormData();
        for (const key in this.form) {
          const value = this.form[key];

          // No agregues valores null o undefined salvo edición para limpiar
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          } else if (value === null && this.empresaId) {
            formData.append(key, "");
          }
        }

        let response;

        if (this.empresaId) {
          formData.append("id", this.empresaId);
          response = await EmpresaService.update(formData);
        } else {
          response = await EmpresaService.store(formData);
        }

        const { data } = response;

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t(data.message),
            icon: "CoffeeIcon",
            variant: "success",
            text: this.$t(data.message),
          },
        });
        this.$emit("refresh");
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 422) {
          const errors = error.response.data.errors;

          // Combinar todos los mensajes en un solo string separados por saltos de línea
          const errorMessages = Object.values(errors).flat().join("\n");

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Errores de validación",
              icon: "AlertCircleIcon",
              variant: "danger",
              text: errorMessages,
            },
          });
        } else {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "CoffeeIcon",
              variant: "danger",
              text: "Error al guardar la empresa.",
            },
          });
        }
      } finally {
        this.removePreloader();
      }
    },
  },
};
</script>
